/**
 * Model creation stepper — inline flow to create model + category + manufacturer.
 * Renders as a modal overlay.
 *
 * Step 1: Select or create Manufacturer
 * Step 2: Name the Model + Model No.
 * Step 3: Select or create Category
 * Step 4: Select Fieldset, create model
 */

import {
  getManufacturers,
  createManufacturer,
  getCategories,
  createCategory,
  createModel,
  getFieldsets,
} from '../snipeit.js';
import { showToast } from '../toast.js';

let overlay = null;

/**
 * Open the model stepper modal.
 * @param {(newModel: {id: number, name: string}) => void} onComplete
 * @param {{ manufacturerName?: string, manufacturerId?: number, modelName?: string, modelNumber?: string }} prefill
 */
export function openModelStepper(onComplete, prefill = {}) {
  // Remove existing overlay if any
  closeModelStepper();

  overlay = document.createElement('div');
  overlay.id = 'model-stepper-overlay';
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 950;
    background: rgba(0,0,0,0.5);
    display: flex; align-items: flex-end; justify-content: center;
  `;

  overlay.innerHTML = `
    <div style="
      background: var(--color-surface);
      border-radius: var(--radius-lg) var(--radius-lg) 0 0;
      width: 100%; max-width: 480px;
      max-height: 80dvh; overflow-y: auto;
      padding: 20px 16px calc(16px + var(--safe-bottom));
    ">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:16px;">
        <h2>Create New Model</h2>
        <button class="btn btn-secondary btn-sm" id="stepper-close">Cancel</button>
      </div>

      <div class="stepper">
        <!-- Step 1: Manufacturer -->
        <div class="stepper-step" id="step-manufacturer">
          <div class="stepper-step-header">Manufacturer</div>
          <div class="form-group">
            <select id="stepper-mfr-select" class="form-control">
              <option value="">Loading...</option>
            </select>
          </div>
          <div style="display:flex; gap:8px; align-items:end;">
            <div class="form-group" style="flex:1; margin-bottom:0;">
              <label for="stepper-mfr-new">Or create new</label>
              <input type="text" id="stepper-mfr-new" class="form-control" placeholder="Manufacturer name" />
            </div>
            <button class="btn btn-secondary btn-sm" id="stepper-mfr-create" style="margin-bottom:0;">Create</button>
          </div>
        </div>

        <!-- Step 2: Model Details -->
        <div class="stepper-step" id="step-model">
          <div class="stepper-step-header">Model Details</div>
          <div class="form-group">
            <label for="stepper-model-name">Model Name</label>
            <input type="text" id="stepper-model-name" class="form-control" placeholder="e.g. ProBook 450 G9" />
          </div>
          <div class="form-group">
            <label for="stepper-model-number">Model No.</label>
            <input type="text" id="stepper-model-number" class="form-control" placeholder="e.g. 450G9-A1B" autocomplete="off" />
          </div>
        </div>

        <!-- Step 3: Category -->
        <div class="stepper-step" id="step-category">
          <div class="stepper-step-header">Category</div>
          <div class="form-group">
            <select id="stepper-cat-select" class="form-control">
              <option value="">Loading...</option>
            </select>
          </div>
          <div style="display:flex; gap:8px; align-items:end;">
            <div class="form-group" style="flex:1; margin-bottom:0;">
              <label for="stepper-cat-new">Or create new</label>
              <input type="text" id="stepper-cat-new" class="form-control" placeholder="Category name" />
            </div>
            <button class="btn btn-secondary btn-sm" id="stepper-cat-create" style="margin-bottom:0;">Create</button>
          </div>
        </div>

        <!-- Step 4: Fieldset + Save -->
        <div class="stepper-step" id="step-fieldset">
          <div class="form-group">
            <label for="stepper-fieldset-select">Fieldset</label>
            <select id="stepper-fieldset-select" class="form-control">
              <option value="" disabled selected>Select fieldset...</option>
            </select>
          </div>
          <button class="btn btn-primary btn-block" id="stepper-save">Create Model</button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  // Prefill model name and number
  if (prefill.modelName) {
    const nameEl = document.getElementById('stepper-model-name');
    if (nameEl) {
      nameEl.value = prefill.modelName;
      nameEl.classList.add('form-control--ai-prefilled');
      nameEl.addEventListener('input', () => nameEl.classList.remove('form-control--ai-prefilled'), { once: true });
    }
  }
  if (prefill.modelNumber) {
    const numEl = document.getElementById('stepper-model-number');
    if (numEl) {
      numEl.value = prefill.modelNumber;
      numEl.classList.add('form-control--ai-prefilled');
      numEl.addEventListener('input', () => numEl.classList.remove('form-control--ai-prefilled'), { once: true });
    }
  }

  // Load data
  loadManufacturers(prefill);
  loadCategories();
  loadFieldsets();

  // ── Close ──
  document.getElementById('stepper-close').addEventListener('click', closeModelStepper);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModelStepper();
  });

  // ── Create manufacturer ──
  document.getElementById('stepper-mfr-create').addEventListener('click', async () => {
    const name = document.getElementById('stepper-mfr-new').value.trim();
    if (!name) return;
    try {
      const res = await createManufacturer({ name });
      const mfr = res.payload || res;
      const select = document.getElementById('stepper-mfr-select');
      const opt = document.createElement('option');
      opt.value = mfr.id;
      opt.textContent = name;
      opt.selected = true;
      select.appendChild(opt);
      document.getElementById('stepper-mfr-new').value = '';
      document.getElementById('step-manufacturer').classList.add('completed');
      showToast('Manufacturer created');
    } catch (err) {
      showToast('Failed: ' + err.message);
    }
  });

  // ── Create category ──
  document.getElementById('stepper-cat-create').addEventListener('click', async () => {
    const name = document.getElementById('stepper-cat-new').value.trim();
    if (!name) return;
    try {
      const res = await createCategory({ name, category_type: 'asset' });
      const cat = res.payload || res;
      const select = document.getElementById('stepper-cat-select');
      const opt = document.createElement('option');
      opt.value = cat.id;
      opt.textContent = name;
      opt.selected = true;
      select.appendChild(opt);
      document.getElementById('stepper-cat-new').value = '';
      document.getElementById('step-category').classList.add('completed');
      showToast('Category created');
    } catch (err) {
      showToast('Failed: ' + err.message);
    }
  });

  // ── Create model ──
  document.getElementById('stepper-save').addEventListener('click', async () => {
    const name = document.getElementById('stepper-model-name').value.trim();
    const mfrId = document.getElementById('stepper-mfr-select').value;
    const catId = document.getElementById('stepper-cat-select').value;
    const fieldsetId = document.getElementById('stepper-fieldset-select').value;
    const modelNumber = document.getElementById('stepper-model-number').value.trim();

    if (!name) {
      showToast('Model name is required');
      return;
    }
    if (!catId) {
      showToast('Category is required');
      return;
    }
    if (!fieldsetId) {
      showToast('Select a fieldset (or None)');
      return;
    }

    const data = { name, category_id: parseInt(catId, 10) };
    if (mfrId) data.manufacturer_id = parseInt(mfrId, 10);
    if (fieldsetId && fieldsetId !== 'none') data.fieldset_id = parseInt(fieldsetId, 10);
    if (modelNumber) data.model_number = modelNumber;

    try {
      const res = await createModel(data);
      const model = res.payload || res;
      closeModelStepper();
      onComplete({ id: model.id, name, fieldset_id: data.fieldset_id ?? null });
    } catch (err) {
      showToast('Failed: ' + err.message);
    }
  });
}

async function loadManufacturers(prefill = {}) {
  try {
    const res = await getManufacturers();
    const select = document.getElementById('stepper-mfr-select');
    if (!select) return;
    select.innerHTML = '<option value="">-- Select --</option>';
    (res.rows || []).forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m.id;
      opt.textContent = m.name;
      select.appendChild(opt);
    });
    // Apply prefill
    if (prefill.manufacturerId) {
      select.value = String(prefill.manufacturerId);
      if (select.value) select.classList.add('form-control--ai-prefilled');
    } else if (prefill.manufacturerName) {
      const match = Array.from(select.options).find(
        o => o.value && o.textContent.toLowerCase() === prefill.manufacturerName.toLowerCase()
      );
      if (match) {
        match.selected = true;
        select.classList.add('form-control--ai-prefilled');
      } else {
        const newInput = document.getElementById('stepper-mfr-new');
        if (newInput) {
          newInput.value = prefill.manufacturerName;
          newInput.classList.add('form-control--ai-prefilled');
          newInput.addEventListener('input', () => newInput.classList.remove('form-control--ai-prefilled'), { once: true });
        }
      }
    }
  } catch {
    const select = document.getElementById('stepper-mfr-select');
    if (select) select.innerHTML = '<option value="">Failed to load</option>';
  }
}

async function loadFieldsets() {
  const select = document.getElementById('stepper-fieldset-select');
  if (!select) return;
  try {
    const res = await getFieldsets();
    select.innerHTML = '<option value="" disabled selected>Select fieldset...</option><option value="none">None</option>';
    (res.rows || []).forEach((f) => {
      const opt = document.createElement('option');
      opt.value = f.id;
      opt.textContent = f.name;
      select.appendChild(opt);
    });
  } catch {
    select.innerHTML = '<option value="" disabled selected>Select fieldset...</option><option value="none">None</option>';
  }
}

async function loadCategories() {
  try {
    const res = await getCategories();
    const select = document.getElementById('stepper-cat-select');
    if (!select) return;
    select.innerHTML = '<option value="">-- Select --</option>';
    (res.rows || []).forEach((c) => {
      const opt = document.createElement('option');
      opt.value = c.id;
      opt.textContent = c.name;
      select.appendChild(opt);
    });
  } catch {
    const select = document.getElementById('stepper-cat-select');
    if (select) select.innerHTML = '<option value="">Failed to load</option>';
  }
}

function closeModelStepper() {
  if (overlay) {
    overlay.remove();
    overlay = null;
  }
}
